export type Direction = 'up' | 'down' | 'left' | 'right';

/** The parts of a `DOMRect` that matter here, so this stays testable without a DOM. */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Candidate<T> {
  item: T;
  box: Box;
}

function centre(box: Box): { x: number; y: number } {
  return { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 };
}

/**
 * Picks the neighbour a player means by pressing `direction`.
 *
 * Three rules, in order of influence:
 *
 *  1. A candidate must lie on the correct side, measured centre to centre.
 *  2. Distance *along* the axis of travel dominates (the ×4), so Down lands in
 *     the next row rather than skipping one to reach a column that happens to
 *     sit closer sideways.
 *  3. Overlapping the current box on the cross axis all but cancels the
 *     remaining penalty, which is what makes a row of action tiles behave like a
 *     row instead of scattering focus diagonally.
 */
export function chooseInDirection<T>(
  from: Box,
  candidates: Candidate<T>[],
  direction: Direction,
): T | null {
  const origin = centre(from);
  const horizontal = direction === 'left' || direction === 'right';

  let best: T | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const { item, box } of candidates) {
    const target = centre(box);

    const along = horizontal
      ? direction === 'right'
        ? target.x - origin.x
        : origin.x - target.x
      : direction === 'down'
        ? target.y - origin.y
        : origin.y - target.y;

    // Not in the direction of travel at all.
    if (along <= 1) continue;

    // And it must clear the current element's *edge*, not merely its centre.
    // Centres alone are far too delicate: a couple of pixels of movement on the
    // focused element — a focus style, a scrollbar appearing, a font swap — is
    // enough to make a same-row neighbour look like it is below, and Down then
    // walks sideways. Anything genuinely in the next row clears the edge by a
    // wide margin.
    const clearsEdge = horizontal
      ? direction === 'right'
        ? target.x > from.right
        : target.x < from.left
      : direction === 'down'
        ? target.y > from.bottom
        : target.y < from.top;
    if (!clearsEdge) continue;

    const across = horizontal ? Math.abs(target.y - origin.y) : Math.abs(target.x - origin.x);
    const overlaps = horizontal
      ? box.bottom > from.top + 1 && box.top < from.bottom - 1
      : box.right > from.left + 1 && box.left < from.right - 1;

    const score = along * 4 + across * (overlaps ? 0.1 : 1);
    if (score < bestScore) {
      bestScore = score;
      best = item;
    }
  }

  return best;
}
